import {
  Component,
  ViewEncapsulation,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { Subscription } from 'rxjs';
import { OdometerModel } from './odometer.model';
import { OdometerConfig, OdometerConfigModel } from './odometer.config';
import { PlatformService } from '../../services/platform.service';
import { defaults, isNullOrUndefined } from '../../helpers/utils';
import { NgStyle } from '@angular/common';
import { ScriptLoaderService } from 'ngx-unificator/services';
import { catchError, tap } from 'rxjs/operators';

declare let Odometer: any;

@Component({
  selector: 'app-odometer',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './odometer.component.html',
  styleUrls: [
    '../../../core/modules/odometer/themes/DEFAULT_THEME.scss',
    '../../../core/modules/odometer/themes/GRADIENT_THEME.scss',
  ],
  imports: [NgStyle],
})
export class OdometerComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  private _platform = inject(PlatformService);
  private _scriptLoader = inject(ScriptLoaderService);

  readonly container = viewChild('container', { read: ElementRef });

  readonly number = input<number>(undefined);
  @Input() config: OdometerConfigModel = {};
  readonly observable = input<Observable<boolean>>(undefined);

  // Individual configuration attributes
  readonly animation = input<string>(undefined);
  readonly format = input<string>(undefined);
  readonly theme = input<string>(undefined);
  readonly value = input<number>(undefined);
  readonly duration = input<number>(undefined);
  readonly auto = input<boolean>(undefined);
  readonly odometerColor = input<string>(undefined);

  private _subscription: Subscription;
  private _odometer: OdometerModel;

  // Available themes
  private _themes: Array<string> = [
    'default', 'gradient',
  ];

  // Start Odometer
  private async _initOdometer() {
    await lastValueFrom(
      this._scriptLoader.load(
        'https://cdn.jsdelivr.net/npm/odometer@0.4.8/odometer.min.js',
      ),
    );

    if (!isNullOrUndefined(this.container)
      && typeof Odometer !== 'undefined') {

      this._odometer = new Odometer({
        el: this.container().nativeElement,
        animation: this.config.animation,
        value: this.config.value,
        duration: this.config.duration,
        format: this.config.format,
        theme: this.config.theme,
      });

      if (!isNullOrUndefined(this.number) && this.config.auto) {
        this._odometer.update(this.number());
      }
    }
  }

  private _initConfig() {

    this.config = defaults(this.config, new OdometerConfig());

    // Animation
    const animation = this.animation();
    if (!isNullOrUndefined(animation)) {
      this.config.animation = animation;
    }

    // Format
    const format = this.format();
    if (!isNullOrUndefined(format)) {
      this.config.format = format;
    }

    // Theme
    const theme = this.theme();
    if (!isNullOrUndefined(theme)) {
      this.config.theme = !this._themes.includes(theme) ? 'default' : theme;
    }

    // Value
    const value = this.value();
    if (!isNullOrUndefined(value)) {
      this.config.value = value;
    }

    // Duration
    const duration = this.duration();
    if (!isNullOrUndefined(duration)) {
      this.config.duration = duration;
    }

    // Auto
    const auto = this.auto();
    if (!isNullOrUndefined(auto)) {
      this.config.auto = auto;
    }

    // Validate theme. If not part of the
    // available themes array, use the default
    if (!this._themes.includes(this.config.theme)) {
      this.config.theme = 'default';
    }
  }

  public ngOnInit(): void {
    if (this._platform.isBrowser) {
      // Bind Observable
      const observable = this.observable();
      if (!isNullOrUndefined(observable) && !this.config.auto) {
        this._subscription = observable.subscribe((trigger: boolean) => {
          if (!isNullOrUndefined(trigger) && trigger) {
            this._odometer.update(this.number());
          }
        });
      }

      // Apply defaults and
      // individual configurations
      this._initConfig();
    }
  }

  public ngOnDestroy() {
    if (this._platform.isBrowser) {
      if (!isNullOrUndefined(this._subscription)) {
        this._subscription.unsubscribe();
      }
    }
  }

  public ngOnChanges() {
    if (this._platform.isBrowser) {
      const number = this.number();
      if (!isNullOrUndefined(number) && !isNullOrUndefined(this._odometer) && this.config.auto) {
        this._odometer.update(number);
      }
    }
  }

  public ngAfterViewInit() {
    if (this._platform.isBrowser) {
      this._initOdometer();
    }
  }

}
